import type { CSSProperties, ReactNode } from 'react';
import type { Anomaly } from '@soc/shared';
import { useGetRulesQuery } from '../../app/api';
import { sevChip, sevColor } from '../../lib/severity';
import { formatTimestamp } from '../../lib/format';
import { RuleIcon } from '../../lib/ruleIcons';
import { useInvestigateAlert } from './useInvestigateAlert';

/** Contents of the anchored popup for a single alert pin. */
export function AlertPopupBody({ alert }: { alert: Anomaly }) {
  const { data: rules } = useGetRulesQuery();
  const rule = rules?.items.find((r) => r.id === alert.ruleId);
  const color = sevColor(alert.severity);
  const { canInvestigate, investigate } = useInvestigateAlert(alert);

  return (
    <div style={{ minWidth: 200, maxWidth: 260 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: rule?.description ? 4 : 10 }}>
        <span style={{ display: 'inline-flex', color }}>
          <RuleIcon ruleId={alert.ruleId} size={15} />
        </span>
        <span style={severityChipStyle(alert.severity)}>{alert.severity}</span>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{alert.ruleName}</span>
      </div>
      {rule?.description && (
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 10px', lineHeight: 1.4 }}>
          {rule.description}
        </p>
      )}
      <DetailRow label="Tower" value={alert.towerId} />
      <DetailRow label="Value" value={alert.value} color={color} />
      <DetailRow label="Time" value={formatTimestamp(alert.timestamp)} />
      <DetailRow label="Coords" value={`${alert.lat.toFixed(4)}, ${alert.lng.toFixed(4)}`} />
      {canInvestigate && (
        <button
          type="button"
          onClick={investigate}
          style={{
            marginTop: 10,
            width: '100%',
            padding: '6px 10px',
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--text-primary)',
            background: 'var(--surface-app)',
            border: '1px solid var(--border-hover)',
            borderRadius: 6,
            cursor: 'pointer',
          }}
        >
          🔍 Investigate
        </button>
      )}
    </div>
  );
}

/** A label / mono-value row inside a popup. */
export function DetailRow({ label, value, color }: { label: string; value: ReactNode; color?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 12, padding: '2.5px 0' }}>
      <span style={{ width: 48, flex: 'none', color: 'var(--text-muted)' }}>{label}</span>
      <span className="mono" style={{ color: color ?? 'var(--text-secondary)', minWidth: 0, wordBreak: 'break-word' }}>
        {value}
      </span>
    </div>
  );
}

export function severityChipStyle(severity: Anomaly['severity']): CSSProperties {
  return {
    fontFamily: 'var(--font-display)',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.8,
    color: sevColor(severity),
    background: sevChip(severity),
    padding: '2px 6px',
    borderRadius: 4,
    textTransform: 'uppercase',
  };
}
